import { Component, DestroyRef, inject, Inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { SiteService } from '@core/api/site.service';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { StatusEnum } from '@shared/constants/site_status.enum';
import { DialogResubmitNoteComponent } from '@shared/dialogs/dialog-resubmit-note/dialog-resubmit-note.component';
import { Site } from '@shared/models';
import { SiteStatusService } from '@core/helper/site-status.service';

export interface DialogData {
  sites: Array<Site>;
  categories: Array<string>;
  buttonTextNo: string;
  buttonTextYes: string;
}

@Component({
  selector: 'fc-yes-no-dialog',
  templateUrl: './dialog-change-site-state.component.html',
  styleUrls: ['./dialog-change-site-state.component.scss'],
  standalone: false
})
export class DialogChangeSiteStateComponent {
  destroyRef = inject(DestroyRef);

  changeSiteStateForm: UntypedFormGroup;
  errorMessage = null;

  constructor(
    public dialogRef: MatDialogRef<DialogChangeSiteStateComponent>,
    public extraDialog: MatDialog,
    readonly formBuilder: UntypedFormBuilder,
    readonly siteService: SiteService,
    readonly siteStatusService: SiteStatusService,
    readonly spinnerService: SpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.createForm();
  }

  createForm() {
    this.changeSiteStateForm = this.formBuilder.group({
      transitionToState: [''],
      clearSubmitDate: [''],
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  doChangeSitesState() {
    this.errorMessage = null;
    const transitionToState = this.changeSiteStateForm.get('transitionToState').value;
    const siteIdList = this.data.sites.map(site => site.id);
    if (
      transitionToState == StatusEnum.REQUIRES_LANDOWNER_REVISION ||
      transitionToState == StatusEnum.REQUIRES_LANDOWNER_APPROVAL ||
      transitionToState == StatusEnum.DOCUMENT_REQUIRED
    ) {
      this.extraDialog
        .open(DialogResubmitNoteComponent)
        .afterClosed()
        .subscribe(note => {
          if (note) {
            this.spinnerService.show('app-spinner');
            this.siteService
              .changeSitesState(siteIdList, transitionToState, !!this.changeSiteStateForm.controls.clearSubmitDate.value, note)
              .pipe(
                finalize(() => this.spinnerService.hide('app-spinner')),
                takeUntilDestroyed(this.destroyRef)
              )
              .subscribe(response => {
                if (response.error) {
                  this.errorMessage = response.error;
                } else {
                  this.dialogRef.close(true);
                }
              });
          }
        });
    } else {
      this.spinnerService.show('app-spinner', 'Changing Site Status');
      this.siteService.changeSitesState(siteIdList, transitionToState, !!this.changeSiteStateForm.controls.clearSubmitDate.value)
        .subscribe(response => {
          this.spinnerService.hide('app-spinner');
          if (response.error) {
            this.errorMessage = response.error;
          } else {
            this.dialogRef.close(true);
          }
        });
    }
  }

  canSubmit() {
    return this.changeSiteStateForm.controls.clearSubmitDate.value || this.changeSiteStateForm.controls.transitionToState.value;
  }
}
